import { Component, Inject, OnDestroy } from "@angular/core";
import {
  AbstractControl,
  Validators,
  FormControl,
  FormGroup,
  ValidatorFn,
} from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { BrandCampaignAddContentDialogData } from "../../../../shared/models/brand-campaign-add-content-dialog-data";
import { BrandCampaign } from "../../../../shared/models/brandCampaign";
import { BrandCampaignCategory } from "../../../../shared/models/brandCampaignCategory";
import { BrandCampaignDetails } from "../../../../shared/models/brandCampaignDetail";
import { BrandCampaignGoogleAdPost } from "../../../../shared/models/brandCampaignGoogleAdPost";
import { PostPlatform } from "../../../../shared/enums/campaignPost.enums";
import { ImageEntity } from "../../../../shared/entities/file/image.entity";
import { DialogService } from "../../../../shared/services/dialog.service";
import { NotificationService } from "../../../../shared/services/notification.service";
import {
  CapitalizationLimit,
  CustomFormValidators,
} from "../../../../shared/validators/custom-form-validators";
import { BrandCampaignPostService } from "../../shared/services/brand-campaign-post.service";
import { UploaderResponseError } from "../../../../shared/components/file-uploader/models/uploader-response-error";
import { AnyToImageEntityMapper } from "../../../../shared/mappers/image.entity.mapper";

interface UploadData {
  platform: string;
  post_id?: string;
}

const GOOGLE_ADS_VALIDATORS: ValidatorFn[] = [
  CustomFormValidators.googleAdsChars,
  CustomFormValidators.twoConsecutiveDots,
  CustomFormValidators.noEmojis,
  CustomFormValidators.noUrlProtocol,
  CustomFormValidators.uppercaseLimit(CapitalizationLimit.GoogleAds),
];

export interface CampaignContentFormGoogleAdDialogData
  extends BrandCampaignAddContentDialogData {
  readonly googleAdPost?: BrandCampaignGoogleAdPost;
}

export type CampaignContentFormGoogleAdDialogResult = undefined;

@Component({
  selector: "app-campaign-content-form-google-ad-dialog",
  templateUrl: "./campaign-content-form-google-ad-dialog.component.html",
  styleUrls: ["./campaign-content-form-google-ad-dialog.component.scss"],
})
export class CampaignContentFormGoogleAdDialogComponent implements OnDestroy {
  public readonly form: FormGroup;
  public saving = false;
  public readonly campaign: BrandCampaign;
  public readonly campaignDetails: BrandCampaignDetails;
  public readonly category: BrandCampaignCategory;
  public googleAdPost: BrandCampaignGoogleAdPost;
  public showHeadlineIndex = 1;
  public showDescriptionIndex = 1;
  public uploaderUrl: string;
  public readonly uploadData: UploadData = {
    platform: PostPlatform.Google,
  };
  public isNewGoogleAdPost: boolean;
  public deletingFileId?: number;
  public showInvalidImageMessage = false;
  public hasRequiredImages = false;
  public readonly maxImages = 18;
  public hasReachedImagesLimit = false;
  public readonly fileUploaderResponseMapper = AnyToImageEntityMapper;

  public headlines: AbstractControl<string>[] = [];
  public descriptions: AbstractControl<string>[] = [];

  private readonly subs = new Subscription();

  constructor(
    private readonly dialogService: DialogService,
    private readonly translateService: TranslateService,
    private readonly brandCampaignPostService: BrandCampaignPostService,
    private readonly notificationService: NotificationService,
    private readonly dialogRef: MatDialogRef<
      CampaignContentFormGoogleAdDialogComponent,
      CampaignContentFormGoogleAdDialogResult
    >,
    @Inject(MAT_DIALOG_DATA) data: CampaignContentFormGoogleAdDialogData,
  ) {
    this.campaign = data.campaign;
    this.campaignDetails = data.campaignDetails;
    this.category = data.category;
    this.isNewGoogleAdPost = !data.googleAdPost;

    if (data.googleAdPost) {
      this.googleAdPost = data.googleAdPost;
      this.uploadData.post_id = this.googleAdPost.id.toString();
      this.checkHasRequiredImages();
    } else {
      this.googleAdPost = new BrandCampaignGoogleAdPost({
        images: [],
        headlineOne: "",
        headlineTwo: "",
        headlineThree: "",
        headlineFour: "",
        headlineFive: "",
        longHeadline: "",
        descriptionOne: "",
        descriptionTwo: "",
        descriptionThree: "",
        descriptionFour: "",
        descriptionFive: "",
      });
    }

    if (this.googleAdPost.headlineFive) {
      this.showHeadlineIndex = 5;
    } else if (this.googleAdPost.headlineFour) {
      this.showHeadlineIndex = 4;
    } else if (this.googleAdPost.headlineThree) {
      this.showHeadlineIndex = 3;
    } else if (this.googleAdPost.headlineTwo) {
      this.showHeadlineIndex = 2;
    }

    if (this.googleAdPost.descriptionFive) {
      this.showDescriptionIndex = 5;
    } else if (this.googleAdPost.descriptionFour) {
      this.showDescriptionIndex = 4;
    } else if (this.googleAdPost.descriptionThree) {
      this.showDescriptionIndex = 3;
    } else if (this.googleAdPost.descriptionTwo) {
      this.showDescriptionIndex = 2;
    }

    this.saving = false;
    this.uploaderUrl =
      this.brandCampaignPostService.getAddCampaignDetailCategoryPostFileUrl(
        this.campaign.id,
        this.campaignDetails.id,
        this.category.id,
      );

    this.form = new FormGroup({
      headlineOne: new FormControl(this.googleAdPost.headlineOne, [
        Validators.required,
        ...GOOGLE_ADS_VALIDATORS,
      ]),
      headlineTwo: new FormControl(this.googleAdPost.headlineTwo, [
        ...GOOGLE_ADS_VALIDATORS,
      ]),
      headlineThree: new FormControl(this.googleAdPost.headlineThree, [
        ...GOOGLE_ADS_VALIDATORS,
      ]),
      headlineFour: new FormControl(this.googleAdPost.headlineFour, [
        ...GOOGLE_ADS_VALIDATORS,
      ]),
      headlineFive: new FormControl(this.googleAdPost.headlineFive, [
        ...GOOGLE_ADS_VALIDATORS,
      ]),
      longHeadline: new FormControl(this.googleAdPost.longHeadline, [
        Validators.required,
        ...GOOGLE_ADS_VALIDATORS,
      ]),
      descriptionOne: new FormControl(this.googleAdPost.descriptionOne, [
        Validators.required,
        ...GOOGLE_ADS_VALIDATORS,
      ]),
      descriptionTwo: new FormControl(this.googleAdPost.descriptionTwo, [
        ...GOOGLE_ADS_VALIDATORS,
      ]),
      descriptionThree: new FormControl(this.googleAdPost.descriptionThree, [
        ...GOOGLE_ADS_VALIDATORS,
      ]),
      descriptionFour: new FormControl(this.googleAdPost.descriptionFour, [
        ...GOOGLE_ADS_VALIDATORS,
      ]),
      descriptionFive: new FormControl(this.googleAdPost.descriptionFive, [
        ...GOOGLE_ADS_VALIDATORS,
      ]),
    });

    this.initFormSubscriptions();
  }

  public ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private initFormSubscriptions(): void {
    this.headlines = [
      this.form.controls.headlineOne,
      this.form.controls.headlineTwo,
      this.form.controls.headlineThree,
      this.form.controls.headlineFour,
      this.form.controls.headlineFive,
    ];

    this.descriptions = [
      this.form.controls.descriptionOne,
      this.form.controls.descriptionTwo,
      this.form.controls.descriptionThree,
      this.form.controls.descriptionFour,
      this.form.controls.descriptionFive,
    ];

    this.subs.add(
      this.form.valueChanges
        .pipe(debounceTime(500), distinctUntilChanged())
        .subscribe(() => {
          this.googleAdPost.headlineOne = this.headlines[0].value;
          this.googleAdPost.headlineTwo = this.headlines[1].value;
          this.googleAdPost.headlineThree = this.headlines[2].value;
          this.googleAdPost.headlineFour = this.headlines[3].value;
          this.googleAdPost.headlineFive = this.headlines[4].value;
          this.googleAdPost.longHeadline =
            this.form.controls.longHeadline.value;
          this.googleAdPost.descriptionOne = this.descriptions[0].value;
          this.googleAdPost.descriptionTwo = this.descriptions[1].value;
          this.googleAdPost.descriptionThree = this.descriptions[2].value;
          this.googleAdPost.descriptionFour = this.descriptions[3].value;
          this.googleAdPost.descriptionFive = this.descriptions[4].value;
        }),
    );
  }

  public showNextHeadline(): void {
    this.showHeadlineIndex++;
  }

  public showNextDescription(): void {
    this.showDescriptionIndex++;
  }

  public getForbiddenCharErrorMessage(): string {
    return (
      this.translateService.instant(
        "brand.campaign.content.googleAdUploadCharError",
      ) +
      ": " +
      CustomFormValidators.GOOGLE_ADS_FORBIDDEN_CHARS.join(" , ")
    );
  }

  public saveContent(): void {
    this.saving = true;

    this.subs.add(
      this.isNewGoogleAdPost
        ? this.brandCampaignPostService
            .createBrandCampaignGooglePost(
              this.campaign.id,
              this.campaignDetails.id,
              this.category.id,
              this.googleAdPost,
            )
            .subscribe(this.handleGoogleAdCreationResponse)
        : this.brandCampaignPostService
            .updateBrandCampaignGooglePost(
              this.campaign.id,
              this.campaignDetails.id,
              this.category.id,
              this.googleAdPost,
            )
            .subscribe(this.handleGoogleAdCreationResponse),
    );
  }

  private handleGoogleAdCreationResponse = {
    next: (googleAdPost: BrandCampaignGoogleAdPost) => {
      this.googleAdPost = googleAdPost;
      if (this.isNewGoogleAdPost) {
        this.category.posts.push(googleAdPost);
      } else {
        this.category.posts = this.category.posts.map((post) =>
          post.id === googleAdPost.id ? googleAdPost : post,
        );
      }
      this.dialogRef.close();
      this.notificationService.success("shared.changesSavedSuccessfully");
    },
    error: () => {
      this.saving = false;
      this.notificationService.error("shared.changesCouldNotBeSaved");
    },
  };

  public handleUploadResponse(file: ImageEntity): void {
    if (!this.hasReachedImagesLimit) {
      this.googleAdPost.images.push(AnyToImageEntityMapper(file));
    }

    this.checkHasRequiredImages();
  }

  public deleteFileConfirm(file: ImageEntity): void {
    this.dialogService.confirm({
      title: "brand.campaign.content.googleAdUploadDeleteImageConfirmTitle",
      message: "brand.campaign.content.googleAdUploadDeleteImageConfirm",
      onConfirm: () => this.deleteGoogleAdPostFile(file),
    });
  }

  private deleteGoogleAdPostFile(file: ImageEntity): void {
    this.deletingFileId = file.id;

    this.subs.add(
      this.brandCampaignPostService
        .deleteCampaignDetailCategoryPostFile(
          this.campaign.id,
          this.campaignDetails.id,
          this.category.id,
          file.id,
        )
        .subscribe({
          next: () => {
            this.deletingFileId = undefined;
            this.googleAdPost.images = this.googleAdPost.images.filter(
              (f) => f.id !== file.id,
            );
            this.checkHasRequiredImages();
            this.notificationService.success(
              "brand.campaign.content.googleAdUploadImageDeleted",
            );
          },
          error: () => {
            this.deletingFileId = undefined;
            this.notificationService.error(
              "brand.campaign.content.googleAdUploadImageDeleteError",
            );
          },
        }),
    );
  }

  private checkHasRequiredImages(): void {
    const landscapeFormats = ["1200x628", "600x315", "600x314"];
    const squareFormats = ["300x300", "512x512", "1200x1200"];

    const totalLandscape = this.googleAdPost.images.filter((image) =>
      landscapeFormats.includes(image.widthXHeight),
    ).length;

    const totalSquare = this.googleAdPost.images.filter((image) =>
      squareFormats.includes(image.widthXHeight),
    ).length;

    this.hasRequiredImages = totalLandscape >= 1 && totalSquare >= 1;
    this.hasReachedImagesLimit =
      this.googleAdPost.images.length >= this.maxImages;
  }

  public isSaveDisabled(): boolean {
    return this.saving || !this.hasRequiredImages || !this.form.valid;
  }

  public fileUploaderErrorHandler(error: UploaderResponseError): void {
    this.notificationService.error(
      this.translateService.instant(error.message, error.interpolateParams),
    );
  }
}
